<template>
    <div class="datasets">
        <h1 class="mb-3">{{ propercase(datablend.type) }} Data Blend Aggregations</h1>
        <p>Once the various disparate data sources have been assembled into a single record, there is often the need to perform additional custom calculations. Using the fields below, you can create unique aggregations that will be appended to this data blend at the end of the data processing process.</p>
        <div class="card mb-3">
            <div class="card-body">
                <h3>Aggreggations</h3>
                <p><em><strong class="text-danger">WARNING: </strong>This is a power-user level screen. We'll build a better UI once we know how we're going to use this and how the various queries end up looking.</em></p>
                <p><a data-bs-toggle="collapse" href="#collapseExample" role="button" aria-expanded="false"
                    aria-controls="collapseExample">View Examples</a></p>
                <div class="collapse border-start border-3 border-secondary ps-3" id="collapseExample">
                    <label class="form-label">A Boolean Value</label>
                    <p class="font-monospace mb-3">SELECT COUNTIF(t.is_online) > 0 FROM UNNEST(transactions) AS t WHERE NOT _is_dummy</p>
                    <label class="form-label">An Aggregated Value (SUM, COUNT, AVG)</label>
                    <p class="font-monospace mb-3">SELECT SUM(IF(t.subchannel = 'PTV App',t.amount,NULL)) FROM UNNEST(transactions) AS t WHERE NOT _is_dummy</p>
                    <label class="form-label">A Full Transaction Record</label>
                    <p class="font-monospace mb-3">SELECT AS STRUCT t.* FROM UNNEST(transactions) AS t WHERE t.channel = 'Email' ORDER BY t.date LIMIT 1</p>
                    <label class="form-label">A Cohort Segmentation</label>
                    <p class="font-monospace">SELECT CASE WHEN COUNTIF(t.channel = 'Email') &gt; 0 AND COUNTIF(t.channel &lt;&gt; 'Email') &gt; 0 THEN 'Email & Others Cohort' WHEN COUNTIF(t.channel = 'Email')&gt;0 THEN 'Email Only' ELSE 'Others Only' END FROM UNNEST(transactions) AS t</p>
                </div>

                <div v-for="(agg, index) in local_db.custom_aggregations" :key="index">
                    <!-- Name -->
                    <div class="mb-3">
                        <label class="form-label">Aggregation #{{index+1}} Name</label>
                        <input 
                            type="text" 
                            class="form-control" 
                            name="agg_name" 
                            v-model="agg.name"
                            :class="{'is-invalid': errors[index].name !== ''}"
                         aria-label="Aggregation #{{index+1}} Name">
                        <div class="text-danger invalid-feedback" v-if="errors[index].name !== ''">
                            {{errors[index].name}}
                        </div>
                    </div>

                    <div :class="{
                        'mb-3': index+1 === local_db.custom_aggregations.length,
                        'mb-5': index+1 !== local_db.custom_aggregations.length
                    }">
                        <label class="form-label">SQL Code</label>
                        <textarea 
                            class="form-control font-monospace" 
                            name="agg_sql" 
                            v-model="agg.sql" 
                            @change="validateSQL(index)"
                            :class="{
                                'is-invalid': errors[index].sql !== '',
                                'is-valid': errors[index].sql === '' && agg.sql.trim() !== '' && !is_validating[index],
                                'is-validating': is_validating[index]
                            }"
                        ></textarea>
                        <div class="text-danger invalid-feedback" v-if="errors[index].sql !== ''">
                            {{errors[index].sql}}
                        </div>
                    </div>
                </div>

                <button 
                    class="btn btn-sm btn-secondary mb-3" 
                    @click="addAggregation()"
                >
                    <span><i class="fas fa-fw fa-plus"></i>Add Aggregation</span>
                </button>
            </div>
        </div>
        <div class="alert alert-danger mb-3 box-content" v-if="form.error">
            There was an error in the fields above. Please make any necessary corrections and try again.
        </div>

        <button class="btn btn-primary mb-3 btn-lg" @click="update()" :disabled="form.busy">
            <img class="icon" src="/img/icons/avid-icons/save.svg" alt=""> Save
        </button>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import Swal from 'sweetalert2';

export default defineComponent({
    name: 'DataBlendAggregations',
    components: {
        Swal
    },
    props: {
        datablend: {
            type: Object,
            required: true
        },
        client: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            local_db: [],
            original_aggregations: [],
            form: {
                busy: false,
                error: false
            },
            errors: [],
            is_validating: []
        };
    },
    beforeMount() {
        this.local_db = this.datablend;
        
        // Store original aggregations for comparison
        this.original_aggregations = this.local_db.custom_aggregations ? 
            JSON.parse(JSON.stringify(this.local_db.custom_aggregations)) : [];

        if(this.local_db.custom_aggregations == null){
            this.local_db.custom_aggregations = [{name: "", sql: ""}];
            this.errors.push({name: "", sql: ""});
            this.is_validating.push(false);
        }
        else {
            //Add blank error messages
            for(let i = 0; i<this.local_db.custom_aggregations.length; i++){
                this.errors.push({name: "", sql: ""});
                this.is_validating.push(false);
            }
        }
    },
    methods: {
        addAggregation() {
            if(this.local_db.custom_aggregations == null)
                this.local_db.custom_aggregations = [];

            this.local_db.custom_aggregations.push({name: "", sql: ""});
            this.errors.push({name: "", sql: ""});
            this.is_validating.push(false);
            //Force refresh the screen
            this.$forceUpdate();
        },
        validateSQL(index) {
            let data = {
                'custom_aggregation': this.local_db.custom_aggregations[index]
            }
            this.errors[index].sql = '';
            this.is_validating[index] = true;

            //Force refresh the screen
            this.$forceUpdate();
            window.axios.post('/api/data_blend/'+this.client.url+'/' + this.local_db.id + '/validate', data)
                .then(response => {
                    if(!response.data.valid) {
                        this.errors[index].sql = response.data.error;
                    }
                    this.is_validating[index] = false;
                    this.$forceUpdate();
                });
        },
        detectChanges() {
            const affectedColumns = [];
            
            // Create maps for easier lookup
            const originalMap = new Map();
            this.original_aggregations.forEach(agg => {
                if (agg.name && agg.name.trim() !== '') {
                    originalMap.set(agg.name, agg);
                }
            });
            
            const currentMap = new Map();
            this.local_db.custom_aggregations.forEach(agg => {
                if (agg.name && agg.name.trim() !== '') {
                    currentMap.set(agg.name, agg);
                }
            });
            
            // Find deleted columns (name no longer exists)
            originalMap.forEach((originalAgg, name) => {
                if (!currentMap.has(name)) {
                    affectedColumns.push(name);
                }
            });
            
            // Find changed columns (name exists but SQL changed)
            originalMap.forEach((originalAgg, name) => {
                if (currentMap.has(name)) {
                    const currentAgg = currentMap.get(name);
                    if (originalAgg.sql !== currentAgg.sql) {
                        affectedColumns.push(name);
                    }
                }
            });
            
            return { affectedColumns };
        },
        async checkColumnUsage() {
            const changes = this.detectChanges();
            
            if (changes.affectedColumns.length === 0) {
                return { hasAffectedPages: false, affectedPages: [] };
            }
            
            try {
                const response = await window.axios.post('/api/data_blend/'+this.client.url+'/' + this.local_db.id + '/check-column-usage', {
                    affected_columns: changes.affectedColumns
                });
                
                return response.data;
            } catch (error) {
                console.error('Error checking column usage:', error);
                return { hasAffectedPages: false, affectedPages: [] };
            }
        },
        async update() {
            //Clear out any empty aggregations
            this.form.error = false;
            for(var i = this.local_db.custom_aggregations.length-1; i >= 0; i--) {
                if(this.local_db.custom_aggregations[i].name == "" &&
                    this.local_db.custom_aggregations[i].sql == ""){
                    this.local_db.custom_aggregations.splice(i, 1);
                    this.errors.splice(i, 1);
                    this.is_validating.splice(i, 1);
                }
                else if(this.local_db.custom_aggregations[i].name == "" ) {
                    this.errors[i].name = 'Enter a unique name or delete the SQL value to remove the aggregation from the data blend.';
                    this.form.error = true;
                }
                else if(this.local_db.custom_aggregations[i].sql == "" ) {
                    this.errors[i].sql = 'Enter BigQuery SQL code above or delete the name to remove the aggregation from the data blend.';
                    this.form.error = true;
                }
                //If the SQL was previously invalidated
                else if(this.errors[i].sql != ""){
                    this.form.error = true;
                }
            }

            if(this.form.error) return;
            
            // Check for column usage before proceeding
            const columnUsage = await this.checkColumnUsage();
            
            if (columnUsage.has_affected_pages === true) {
                // Show warning dialog with affected pages
                const pagesList = columnUsage.affected_pages.map(page => 
                    `• ${page.type}: ${page.name}`
                ).join('<br>');
                
                const result = await Swal.fire({
                    title: 'Warning: Reports, Segments, and Dimensions Will Be Affected',
                    html: `<div class="text-start">The following changes will affect existing assets:<br><br>${pagesList}<br><br>These reports, segments, and dimensions may need to be updated or will be deleted. Do you want to continue?</div>`,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Yes, Continue',
                    cancelButtonText: 'Cancel',
                    customClass: {
                        popup: 'swal-warning'
                    },
                });
                
                if (!result.isConfirmed) {
                    return;
                }
            }
            
            this.form.busy = true;

            // Prepare the data with affected items for deletion
            const updateData = {
                ...this.local_db,
                affected_items: columnUsage.has_affected_pages ? columnUsage.affected_pages : []
            };

            window.axios.put('/api/data_blend/'+this.client.url+'/' + this.local_db.id, updateData)
                .then(response => {
                    this.form.busy = false;
                    Swal.fire({
                        title: "Success!",
                        html: "The aggregations have been added to the data blend and the refresh process has been started. The new columns should be available in a few minutes for segmentation and analysis.",
                        icon: "success",
                        customClass: {
                            popup: 'swal-success'
                        },
                    }).then(() => {
                        window.location.replace("/settings/"+this.client.url+"/data_blends");
                    });
                })
                .catch(error => {
                    this.form.busy = false;
                    console.error('Error updating data blend:', error);
                    Swal.fire({
                        title: "Error!",
                        html: "There was an error updating the data blend. Please try again.",
                        icon: "error",
                        customClass: {
                            popup: 'swal-error'
                        },
                    });
                });
        }
    }
});
</script>